import { Component, OnDestroy, OnInit } from '@angular/core';

import { AnchorDirective, CurrencyPipe, DynamicHtmlDirective } from '@frontend/vanilla/core';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { BrandType, Config, Country, InputType, LiveChatResponse } from '../../../../config/appConstants';
import { SessionInfo } from '../../../../model/common.model';
import { MessageType } from '../../../../model/message.model';
import { BehaviorDataService } from '../../../../service/behaviorData.service';
import { ChatService } from '../../../../service/chat.service';
import { DataService } from '../../../../service/data.service';
import { DepositService } from '../../../../service/deposit.service';
import { EventService } from '../../../../service/event.service';
import { GtmService } from '../../../../service/gtm.service';
import { LoaderService } from '../../../../service/loader.service';
import { UserDataService } from '../../../../service/userDataService';
import { ResponseStatus } from '../../../../shared/error.constants';
import { MessageComponent } from '../messages/message-component';

@Component({
    selector: 'app-deposit-success',
    templateUrl: './deposit-success.component.html',
    styleUrls: ['./deposit-success.component.scss'],
    imports: [MessageComponent, TranslateModule, CurrencyPipe, AnchorDirective, DynamicHtmlDirective],
})
export class DepositSuccessComponent implements OnInit, OnDestroy {
    $subscriptions: Subscription[] = [];
    isTransactionSuccess: boolean;
    isCoralBetslipQD: boolean;
    hideSuccessPage: boolean = false;
    response: any;
    timeoutInterval: number;
    interval: any;
    paymentMethodDisplayName: string;
    isCountryUK: boolean;
    pullchatEnabled: boolean;
    zendeskEnabled: boolean;
    showChatNotAvailable: boolean;
    hideChat: boolean;
    isHundredPercentageHardStopReached: boolean;
    isFullStakeAmountNotMet: any;
    cpccContactLink: any;
    gamblingControlURL: string;
    private sessionInfo: SessionInfo;
    currencyGBP: string = 'GBP';
    constructor(
        private depositService: DepositService,
        private userDataService: UserDataService,
        private chatService: ChatService,
        private gtmService: GtmService,
        private eventService: EventService,
        private behaviorData: BehaviorDataService,
        private dataService: DataService,
        private loaderService: LoaderService,
    ) {
        this.sessionInfo = this.dataService.sessionInfo;
        this.response = this.depositService?.submitResponse;
        this.$subscriptions.push(
            this.behaviorData.isFullStakeAmountNotMet.subscribe((data) => {
                this.isFullStakeAmountNotMet = data;
                if (this.response?.status === ResponseStatus.SUCCESS) {
                    this.isTransactionSuccess = true;
                    const stake = +this.sessionInfo?.stake;
                    const brandId = this.sessionInfo?.brandId;
                    if (stake) {
                        if ([BrandType.CORAL, BrandType.LADBROKEUK, BrandType.LADBROKEDE].includes(brandId)) {
                            this.hideSuccessPage = !this.isFullStakeAmountNotMet;
                        } else if (this.sessionInfo?.version === 'v2') {
                            this.hideSuccessPage = true;
                        }
                    }
                    this.isCoralBetslipQD = [BrandType.CORAL, BrandType.LADBROKEUK, BrandType.LADBROKEDE].includes(brandId) && stake > 0;
                    if (this.response?.hardStopLTMPercentage === '100') {
                        this.isHundredPercentageHardStopReached = true;
                    }
                    if (this.response?.hardStopMonthlyPercentage <= 100) {
                        this.gtmService.raiseEventForAffordabilitySuccessMsg(
                            'request higher limit',
                            this.response?.hardStopMonthlyPercentage,
                            Number(this.response?.hardStopLimit) / 1000,
                        );
                    }
                    if ([BrandType.CORAL, BrandType.LADBROKEUK, BrandType.LADBROKEDE].includes(brandId)) {
                        this.gtmService.raiseEventForDepositSuccessload(this.response?.paymentMethod);
                    }
                    // Deposit Success  QD.
                    const depositSuccessQDData = {
                        eventName: 'depositSuccessQD',
                        eventData: {
                            amount: this.response?.amount?.value,
                            currency: this.response?.amount?.currencyCode,
                            type: this.response?.paymentMethod,
                        },
                    };
                    this.depositService.postMessageToParent(depositSuccessQDData, '*');

                    this.gtmService.raiseEvent('deposit', 'DepositSuccess', this.response?.paymentMethod);
                    const url =
                        'bwinex://?event=success&type=' +
                        this.response?.paymentMethod +
                        '&currency=' +
                        this.response?.amount?.currencyCode +
                        '&amount=' +
                        this.response?.amount?.value +
                        '&sso=' +
                        this.userDataService?.userData?.clientInfo?.sessionKey;
                    this.eventService.raiseDepositEvent(url);
                    this.eventService.raiseUpdateBalanceEvent();
                    if (!(this.isFullStakeAmountNotMet && stake)) {
                        this.eventService.raiseCloseEvent(false);
                    }
                    if (this.response?.adaDepositStatus?.depositBlocked) {
                        this.gtmService.raiseEventForUnusualDeposit('unusualDepositInterventionMessage', 'quick deposit', null);
                    }
                    if (this.response?.successMessageInfo?.showAffNetLossMsg === 'Y') {
                        this.gtmService.raiseEventForAffordabilityMsgBannerLoad(
                            'aff ltm limit',
                            'user approaching ltm limit banner',
                            'user approaching ltm limit banner',
                            this.userDataService?.userData?.configParams?.svoc_player_level,
                            null,
                            null,
                            null,
                        );
                    }
                }
            }),
        );
    }

    public get messageType(): typeof MessageType {
        return MessageType;
    }

    ngOnInit() {
        this.initialize();
    }

    initialize() {
        this.loaderService.isLoading.next(false);
        this.behaviorData.changeCVV2('');
        this.paymentMethodDisplayName = this.response?.paymentMethod;
        if (this.paymentMethodDisplayName == InputType.MLIFE) {
            this.paymentMethodDisplayName = 'MGM Rewards';
        }
        const userData = this.userDataService?.userData;
        this.isCountryUK = userData?.userInfo?.country === Country.UK;
        this.pullchatEnabled = userData?.configParams?.pullchatEnabled === Config.YES;
        this.zendeskEnabled = userData?.configParams?.zendeskEnabled === Config.YES;
        this.hideChat = this.sessionInfo?.['hideChat'] == 'true' ? true : false;
        if (!this.isCountryUK) {
            this.cpccContactLink = userData?.configParams?.adaDepositContactUsUrl;
        }
        if (this.isCountryUK) {
            this.gamblingControlURL = userData?.configParams?.game_limits_url || '';
        }
    }

    monthlyLimit(event: any) {
        if (event?.target?.tagName.toLowerCase() == 'u') {
            this.gtmService.raiseEventForAffordabilitySuccessMsgLinks(
                'request higher limit',
                this.response?.hardStopMonthlyPercentage,
                Number(this.response?.hardStopLimit) / 1000,
                'monthlyLimit',
                this.response?.hardstopInfoLink,
                this.response?.playerLevel,
            );
        }
    }

    triggerChat() {
        this.chatService.triggerChat(this.handleLiveChatResponse);
        this.gtmService.raiseEventForUnusualDeposit('interventionLiveChat', 'quick deposit', null);
    }

    handleLiveChatResponse(response?: any) {
        if (response && [LiveChatResponse.LiveChatResponse_1001, LiveChatResponse.LiveChatResponse_1000].includes(response)) {
            this.showChatNotAvailable = true;
        }
    }

    lossLimitDepositCapClick(event: any) {
        if (event?.target?.tagName.toLowerCase == 'a') {
            this.gtmService.raiseEventForAffordabilityNetLossDepositCapClick(
                this.userDataService?.userData?.configParams?.svoc_player_level,
                this.response?.successMessageInfo?.increaseLimitsUrl,
            );
        }
    }

    ngOnDestroy(): void {
        this.$subscriptions.map((subscription) => subscription.unsubscribe());
    }
}
